import React, { useEffect, useRef } from 'react';
import { ConfettiBackground } from '../components/ThemeEffects';
import { useNavigate } from 'react-router-dom';
import { Microphone, VideoCamera, Timer, Star, Check, ArrowRight, Camera, ShieldCheck, DeviceMobile, CloudArrowUp, Heart, MagicWand } from '@phosphor-icons/react';

import { EventDropNav } from '../components/EventDropLogo';

const GuestbookPage = () => {
  const navigate = useNavigate();
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('gb-visible');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.gb-fade').forEach(el => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  const goCheckout = () => navigate('/checkout?add=guestbook');

  return (
    <div className="min-h-screen bg-[#15161B] text-white" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="guestbook-page">
      <ConfettiBackground />

      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#15161B]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <EventDropNav onClick={() => navigate('/')} />
          <button onClick={goCheckout} className="gb-cta px-6 py-2.5 rounded-2xl text-sm font-bold text-white transition-all hover:scale-105" data-testid="nav-guestbook-cta">
            Add Guestbook - £39
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="pt-32 pb-20 px-6" data-testid="gb-hero">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#B89762]/50/20 bg-[#B89762]/5 text-sm text-[#B89762] mb-6">
              <Heart size={14} weight="fill" />
              EventDrop Add-on
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-6" data-testid="gb-hero-title">
              Video Guestbook<br />
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #38BDF8, #818CF8)' }}>
                for Your Event
              </span>
            </h1>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed max-w-lg">
              Let guests leave heartfelt audio & video messages. Set up a tablet, and they tap to record. It's that simple.
            </p>
            <button onClick={goCheckout} className="gb-cta px-10 py-5 rounded-2xl text-lg font-bold text-white transition-all hover:scale-105 shadow-lg" data-testid="gb-hero-cta">
              GET GUESTBOOK - £39
            </button>
            <p className="text-sm text-gray-600 mt-4">One-time. Works with EventDrop Capture.</p>
          </div>

          {/* Tablet Preview */}
          <div className="relative flex justify-center">
            <div className="gb-tablet-frame" data-testid="gb-tablet-preview">
              <div className="gb-tablet-screen">
                {/* Wedding-style preview */}
                <div className="gb-preview-bg gb-preview-wedding">
                  <h2 className="gb-preview-title" style={{ fontFamily: "'Playfair Display', serif" }}>Leave a Message</h2>
                  <div className="flex gap-4 mt-6">
                    <div className="gb-preview-btn" style={{ background: 'linear-gradient(180deg, #F87171, #EF4444)' }}>
                      <Microphone size={28} weight="bold" className="text-white" />
                      <span className="text-white text-xs font-semibold mt-1">Record Audio</span>
                    </div>
                    <div className="gb-preview-btn" style={{ background: 'linear-gradient(180deg, #38BDF8, #0EA5E9)' }}>
                      <VideoCamera size={28} weight="bold" className="text-white" />
                      <span className="text-white text-xs font-semibold mt-1">Record Video</span>
                    </div>
                  </div>
                  <p className="text-white/80 text-xs mt-4">Tap to leave a 30 second message!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6 gb-fade" data-testid="gb-how-it-works">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4 tracking-tight">Effortlessly Simple</h2>
          <p className="text-gray-500 text-center mb-16 max-w-lg mx-auto">Guests don't need an app, account, or instructions. Just tap and talk.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: '01', title: 'Place a Tablet', desc: 'Set up any tablet at your venue with your custom guestbook screen.', icon: <DeviceMobile size={28} weight="bold" /> },
              { num: '02', title: 'Guests Tap to Record', desc: 'They choose audio or video, record a 30-second message, and it auto-saves.', icon: <Microphone size={28} weight="bold" /> },
              { num: '03', title: 'Relive the Moments', desc: 'All messages are saved to your event. Download or watch anytime.', icon: <CloudArrowUp size={28} weight="bold" /> },
            ].map((step, i) => (
              <div key={i} className="relative rounded-2xl border border-white/10 bg-[#15161B] p-8 hover:border-[#B89762]/50/30 transition-colors">
                <span className="text-5xl font-black text-white/5 absolute top-4 right-6">{step.num}</span>
                <div className="w-12 h-12 rounded-xl mb-5 flex items-center justify-center text-[#B89762]" style={{ background: 'rgba(56,189,248,0.1)' }}>
                  {step.icon}
                </div>
                <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 px-6 gb-fade" data-testid="gb-features">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4 tracking-tight">Built for Real Events</h2>
          <p className="text-gray-500 text-center mb-16 max-w-lg mx-auto">No confusing flows. No setup headaches. Just works.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { icon: <Timer size={22} weight="bold" />, title: '30-Second Auto-Timer', desc: 'Messages auto-stop and save after 30 seconds.' },
              { icon: <MagicWand size={22} weight="bold" />, title: 'Customizable Screen', desc: 'Design your guestbook screen with presets or your own branding.' },
              { icon: <Microphone size={22} weight="bold" />, title: 'Audio & Video', desc: 'Guests choose between a quick voice note or a video message.' },
              { icon: <CloudArrowUp size={22} weight="bold" />, title: 'Auto-Save', desc: 'No preview, no confirm. Messages save instantly and screen resets.' },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-4 rounded-2xl border border-white/10 bg-[#15161B] p-6 hover:border-[#B89762]/50/20 transition-colors">
                <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center text-[#B89762]" style={{ background: 'rgba(56,189,248,0.1)' }}>
                  {f.icon}
                </div>
                <div>
                  <h4 className="font-bold mb-1">{f.title}</h4>
                  <p className="text-gray-500 text-sm">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCREEN BUILDER PREVIEW */}
      <section className="py-24 px-6 gb-fade" data-testid="gb-builder-preview">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4 tracking-tight">Design Your Guestbook Screen</h2>
          <p className="text-gray-500 mb-12 max-w-lg mx-auto">Choose from preset themes or build your own. Upload backgrounds, change fonts, colors — all with a live preview.</p>

          <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto">
            {/* Wedding */}
            <div className="rounded-2xl overflow-hidden border border-white/10 hover:border-[#B89762]/50/30 transition-all hover:scale-105 cursor-pointer">
              <div className="aspect-[3/4] gb-preview-wedding flex flex-col items-center justify-center p-4">
                <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Leave a Message</p>
                <div className="flex gap-2 mt-3">
                  <div className="w-8 h-8 rounded-lg bg-red-400/80 flex items-center justify-center"><Microphone size={14} weight="bold" className="text-white" /></div>
                  <div className="w-8 h-8 rounded-lg bg-sky-400/80 flex items-center justify-center"><VideoCamera size={14} weight="bold" className="text-white" /></div>
                </div>
              </div>
              <div className="px-4 py-3 bg-[#15161B] text-center">
                <p className="text-xs font-bold text-[#B89762]">Wedding</p>
              </div>
            </div>

            {/* Party */}
            <div className="rounded-2xl overflow-hidden border border-white/10 hover:border-[#C29D52]/30 transition-all hover:scale-105 cursor-pointer">
              <div className="aspect-[3/4] gb-preview-party flex flex-col items-center justify-center p-4">
                <p className="text-white text-sm font-bold">Drop a Message</p>
                <div className="flex gap-2 mt-3">
                  <div className="w-8 h-8 rounded-lg bg-[#B89762]/80 flex items-center justify-center"><Microphone size={14} weight="bold" className="text-white" /></div>
                  <div className="w-8 h-8 rounded-lg bg-amber-500/80 flex items-center justify-center"><VideoCamera size={14} weight="bold" className="text-white" /></div>
                </div>
              </div>
              <div className="px-4 py-3 bg-[#15161B] text-center">
                <p className="text-xs font-bold text-[#B89762]">Party</p>
              </div>
            </div>

            {/* Corporate */}
            <div className="rounded-2xl overflow-hidden border border-white/10 hover:border-blue-500/30 transition-all hover:scale-105 cursor-pointer">
              <div className="aspect-[3/4] gb-preview-corporate flex flex-col items-center justify-center p-4">
                <p className="text-slate-700 text-sm font-bold">Leave Your Feedback</p>
                <div className="flex gap-2 mt-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/80 flex items-center justify-center"><Microphone size={14} weight="bold" className="text-white" /></div>
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/80 flex items-center justify-center"><VideoCamera size={14} weight="bold" className="text-white" /></div>
                </div>
              </div>
              <div className="px-4 py-3 bg-[#15161B] text-center">
                <p className="text-xs font-bold text-blue-400">Corporate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-16 px-6 gb-fade" data-testid="gb-social-proof">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex justify-center gap-1 mb-4">
            {[1,2,3,4,5].map(i => <Star key={i} size={18} weight="fill" className="text-[#B89762]" />)}
          </div>
          <p className="text-gray-300 italic text-base mb-3">
            "We watched every message the next morning. Half the room was in tears. Best add-on decision we made."
          </p>
          <p className="text-gray-600 text-sm">— Emma & James, Wedding</p>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 px-6 gb-fade" data-testid="gb-pricing">
        <div className="max-w-lg mx-auto">
          <div className="rounded-3xl border-2 border-[#B89762]/50/30 bg-gradient-to-b from-[#B89762]/5 to-transparent p-10 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#B89762]/10 text-[#B89762] text-xs font-bold mb-6">
              ADD-ON
            </div>
            <h3 className="text-2xl font-black mb-2">EventDrop Guestbook</h3>
            <p className="text-gray-500 mb-6 text-sm">Audio & video messages from your guests</p>
            <p className="text-5xl font-black mb-1">£39</p>
            <p className="text-gray-600 text-sm mb-8">One-time payment</p>

            <ul className="space-y-3 text-left max-w-xs mx-auto mb-8">
              {[
                'Unlimited guest messages',
                'Audio & video recording',
                '3 preset themes included',
                'Custom screen builder',
                'Auto-save (no guest effort)',
                'Download all messages',
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-gray-300">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #38BDF8, #818CF8)' }}>
                    <Check size={10} weight="bold" className="text-white" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>

            <button onClick={goCheckout} className="w-full gb-cta py-5 rounded-2xl text-lg font-bold text-white transition-all hover:scale-[1.02] shadow-lg" data-testid="gb-pricing-cta">
              Add Guestbook to Your Event
            </button>
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-600">
              <ShieldCheck size={16} weight="bold" />
              <span>Secure payment via Stripe</span>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-28 px-6 gb-fade" data-testid="gb-final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-6 tracking-tight">Don't miss the words<br />you didn't hear</h2>
          <p className="text-gray-400 text-lg mb-10 max-w-lg mx-auto">Every guest has something to say. Give them a way to say it.</p>
          <button onClick={goCheckout} className="gb-cta px-10 py-5 rounded-2xl text-lg font-bold text-white transition-all hover:scale-105 shadow-lg" data-testid="gb-final-cta-button">
            GET GUESTBOOK - £39
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <EventDropNav onClick={() => navigate('/')} />
          <p className="text-gray-600 text-sm">&copy; 2026 EventDrop. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        .gb-fade { opacity: 0; transform: translateY(20px); transition: opacity 0.6s ease-out, transform 0.6s ease-out; }
        .gb-visible { opacity: 1; transform: translateY(0); }
        .gb-cta { background: linear-gradient(135deg, #38BDF8, #818CF8); box-shadow: 0 0 30px rgba(56,189,248,0.15), 0 0 60px rgba(129,140,248,0.08); }
        .gb-cta:hover { box-shadow: 0 0 40px rgba(56,189,248,0.25), 0 0 80px rgba(129,140,248,0.12); }

        .gb-tablet-frame {
          width: 320px; height: 440px;
          background: #1a1a1a;
          border-radius: 24px;
          padding: 12px;
          box-shadow: 0 25px 60px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.05);
        }
        .gb-tablet-screen {
          width: 100%; height: 100%;
          border-radius: 14px;
          overflow: hidden;
        }
        .gb-preview-bg {
          width: 100%; height: 100%;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 24px;
        }
        .gb-preview-wedding {
          background: linear-gradient(180deg, rgba(244,114,182,0.3) 0%, rgba(251,191,36,0.15) 50%, rgba(120,53,15,0.4) 100%),
                      radial-gradient(circle at 30% 20%, rgba(251,191,36,0.3), transparent 50%),
                      radial-gradient(circle at 70% 80%, rgba(244,114,182,0.2), transparent 50%),
                      #2D1B2E;
        }
        .gb-preview-party {
          background: linear-gradient(135deg, #0F0723 0%, #1A0A3E 50%, #0D0D0D 100%),
                      radial-gradient(circle at 20% 50%, rgba(168,85,247,0.3), transparent 50%),
                      radial-gradient(circle at 80% 30%, rgba(245,158,11,0.2), transparent 50%);
        }
        .gb-preview-corporate {
          background: linear-gradient(180deg, #F1F5F9, #E2E8F0);
        }

        .gb-preview-title { font-size: 22px; font-weight: 700; color: white; text-shadow: 0 2px 8px rgba(0,0,0,0.3); }
        .gb-preview-btn {
          width: 80px; height: 80px;
          border-radius: 16px;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          box-shadow: 0 4px 20px rgba(0,0,0,0.25);
          transition: transform 0.2s;
        }
        .gb-preview-btn:hover { transform: scale(1.05); }
      `}</style>
    </div>
  );
};

export default GuestbookPage;
